// LeadRoutingPage.tsx — Lead Routing hub (Lead Router v2, Wave 3 / S6, AI-xfl2).
// The person/team-centric authoring surface. Screens M1 (Coverage Matrix, landing),
// M2 (Teams), M3 (Team detail → links S8's M6 dealer screen), M4 (Assignment
// Wizard), M5 (Escalation), M7 (Simulator, reuses SimulatorDrawer), Advanced
// (demotes today's rule tables). Master ON/OFF toggle is prominent; DEFAULT OFF —
// this page never enables auto-routing on its own.
//
// Dealer surfaces (M6/M9) are S8's — M3 deep-links to /dealer-management; it does
// not build a dealer editor here.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight, AlertCircle, RefreshCcw, ShieldAlert, Users, Grid3x3,
  TrendingUp, PlayCircle, SlidersHorizontal, ArrowLeft, X, Store,
  Wrench, Ruler, Handshake,
} from 'lucide-react';
import {
  leadRouterApi, leadRouterV2Api,
  type CoverageData, type RouterStaff,
  type CoverageMatrixV2, type RoutingProductGroupV2, type EscalationConfigV2, type DeescalationConfigV2,
} from '@/api/leadRouter';
import {
  classifyMatrixCell, buildBulkPayload, emptyTerritoryProducts,
  stateTriState, toggleState, toggleDistrict, applyRadiusExpansion, districtKey,
  type WizardState, type CellTone,
} from './leadRoutingLogic';
import { SimulatorDrawer, StructuralRulesPanel, Toggle } from './LeadRouterPage';
import { CoverageMatrixV2 as CoverageMatrixV2View } from './coverage/CoverageMatrixV2';
import { DealerTerritoryTab } from './coverage/DealerTerritoryTab';
import { PartyCoverageTab } from './coverage/PartyCoverageTab'; // SQ3 ③.4
import { dealerAssignErrorMessage } from './coverage/dealerGuard';

type Tab = 'coverage' | 'teams' | 'escalation' | 'simulator' | 'advanced' | 'dealer-territory'
  | 'plumber-coverage' | 'architect-coverage' | 'affiliate-coverage'; // SQ3 ③.4

const TONE_BG: Record<CellTone, string> = {
  owned: 'var(--forest-100, #d6ecd9)',
  gap: 'repeating-linear-gradient(45deg, #f6d5d5, #f6d5d5 5px, #fbe4e4 5px, #fbe4e4 10px)',
  overlap: 'var(--warn-bg, #f7eccf)',
  triage: 'var(--violet-50, #ece7fb)',
};
const TONE_FG: Record<CellTone, string> = {
  owned: 'var(--forest, #2f7d3b)',
  gap: 'var(--danger, #b5433a)',
  overlap: 'var(--warn, #a5741a)',
  triage: 'var(--violet, #6b4fd8)',
};

export function LeadRoutingPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('coverage');
  const [coverage, setCoverage] = useState<CoverageData | null>(null);
  const [matrix, setMatrix] = useState<CoverageMatrixV2 | null>(null);
  const [groups, setGroups] = useState<RoutingProductGroupV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingToggle, setSavingToggle] = useState(false);
  const [simOpen, setSimOpen] = useState(false);
  const [wizard, setWizard] = useState<{ layer: 'EXEC' | 'MANAGER'; member: RouterStaff | null } | null>(null);
  const [teamManagerId, setTeamManagerId] = useState<string | null>(null);
  // Coverage-panel overlays (spec §4): territory panel bubbles up manager+dealer edits
  // to reuse the existing M5 escalation editor + dealer modal as overlays.
  const [escalationOverlay, setEscalationOverlay] = useState<string | null>(null);
  const [dealerOverlay, setDealerOverlay] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [cov, mtx, grp] = await Promise.all([
        leadRouterApi.getCoverage(),
        leadRouterV2Api.getCoverageMatrix().catch(() => null),
        leadRouterV2Api.listProductGroups().catch(() => []),
      ]);
      setCoverage(cov);
      setMatrix(mtx);
      setGroups(grp);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load Lead Routing');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);

  const autoOn = coverage?.settings.autoRouterEnabled ?? false;
  const setAuto = async (v: boolean) => {
    setSavingToggle(true);
    try {
      await leadRouterApi.updateSettings({ autoRouterEnabled: v });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to update master toggle');
    } finally {
      setSavingToggle(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, color: 'var(--ink-3)', fontSize: 14 }}>Loading Lead Routing…</div>;
  }
  if (error || !coverage) {
    return (
      <div style={{ padding: 40 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>
          <AlertCircle size={16} /> {error ?? 'No data'}
        </div>
        <button type="button" onClick={load} style={btnStyle}>
          <RefreshCcw size={13} /> Retry
        </button>
      </div>
    );
  }

  const TABS: Array<{ key: Tab; label: string; icon: any }> = [
    { key: 'coverage', label: 'Coverage', icon: Grid3x3 },
    { key: 'dealer-territory', label: 'Dealer Territory', icon: Store },
    // SQ3 ③.4 — reference-party coverage tabs (read-only gap matrices).
    { key: 'plumber-coverage', label: 'Plumbers', icon: Wrench },
    { key: 'architect-coverage', label: 'Architects', icon: Ruler },
    { key: 'affiliate-coverage', label: 'Affiliates', icon: Handshake },
    { key: 'teams', label: 'Teams', icon: Users },
    { key: 'escalation', label: 'Escalation', icon: TrendingUp },
    { key: 'simulator', label: 'Simulator', icon: PlayCircle },
    { key: 'advanced', label: 'Advanced', icon: SlidersHorizontal },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--ivory-1)' }}>
      {/* ── Header + master toggle ── */}
      <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--line)', background: 'var(--ivory-0)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--ink-3)', marginBottom: 6 }}>
          <span>Admin</span><ChevronRight size={11} style={{ opacity: 0.5 }} />
          <span>Builders</span><ChevronRight size={11} style={{ opacity: 0.5 }} />
          <span style={{ color: 'var(--ink-1)' }}>Lead Routing</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink-0)', margin: 0, letterSpacing: -0.3 }}>Lead Routing</h1>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 3 }}>Team-centric auto-router — coverage, assignments, escalation &amp; dealers</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: autoOn ? 'var(--forest)' : 'var(--ink-3)' }}>
              Auto-router {autoOn ? 'ON' : 'OFF'}
            </span>
            <div style={{ opacity: savingToggle ? 0.5 : 1, pointerEvents: savingToggle ? 'none' : 'auto' }}>
              <Toggle on={autoOn} onChange={setAuto} />
            </div>
          </div>
        </div>
        {!autoOn && (
          <div data-testid="ar-banner-off" style={{
            marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 10,
            background: 'var(--warn-bg, #f7eccf)', border: '1px solid #EBD9B0', borderRadius: 10,
            padding: '9px 13px', color: 'var(--warn, #a5741a)', fontSize: 12.5,
          }}>
            <ShieldAlert size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <div><b>Auto-router is OFF</b> — new leads route manually (via triage). Turn on to activate automatic routing.</div>
          </div>
        )}
      </div>

      {/* ── Tab bar ── */}
      <div style={{ display: 'flex', gap: 4, padding: '8px 20px 0', borderBottom: '1px solid var(--line)', background: 'var(--ivory-0)', flexShrink: 0 }}>
        {TABS.map((t) => {
          const on = tab === t.key;
          const Icon = t.icon;
          return (
            <button key={t.key} type="button" onClick={() => { setTab(t.key); setTeamManagerId(null); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px',
                fontSize: 13, fontWeight: on ? 700 : 500, cursor: 'pointer', border: 'none',
                background: 'transparent', color: on ? 'var(--ink-0)' : 'var(--ink-3)',
                borderBottom: on ? '2px solid var(--forest)' : '2px solid transparent',
              }}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '20px 24px' }}>
        {tab === 'coverage' && (
          <CoverageMatrixV2View
            staff={coverage.staff}
            staffGraph={coverage.staffGraph}
            groupLabels={Object.fromEntries((groups.length ? groups : deriveGroupsFromMatrix(matrix)).map((g) => [g.key, g.label]))}
            fallbackUserId={coverage.staff.find((s) => /deepika/i.test(s.name))?.id ?? null}
            onEditEscalation={(managerId) => setEscalationOverlay(managerId)}
            onEditDealers={(managerId) => managerId && setDealerOverlay(managerId)}
          />
        )}
        {tab === 'dealer-territory' && (
          <DealerTerritoryTab dealers={coverage.dealers} />
        )}
        {tab === 'plumber-coverage' && <PartyCoverageTab layer="PLUMBER" label="Plumber" />}
        {tab === 'architect-coverage' && <PartyCoverageTab layer="ARCHITECT" label="Architect" />}
        {tab === 'affiliate-coverage' && <PartyCoverageTab layer="AFFILIATE" label="Affiliate" />}
        {tab === 'teams' && (
          teamManagerId
            ? <TeamDetail coverage={coverage} managerId={teamManagerId} onBack={() => setTeamManagerId(null)}
                onEditMember={(m) => setWizard({ layer: 'EXEC', member: m })} openWizard={setWizard} navigate={navigate} reload={load} />
            : <TeamsTab coverage={coverage} onOpenTeam={setTeamManagerId} />
        )}
        {tab === 'escalation' && <EscalationTab coverage={coverage} groups={groups} />}
        {tab === 'simulator' && (
          <div>
            <SectionHead title="Simulator — trace a lead" sub="Runs the v2 resolver against RoutingAssignment: category → EXEC territory (Layer 1) → manager escalation (Layer 2) → dealer overlay (Layer 3). Read-only — nothing is assigned." />
            <button type="button" onClick={() => setSimOpen(true)} style={{ ...btnStyle, background: 'var(--forest)', color: '#fff', border: 'none' }}>
              <PlayCircle size={14} /> Trace a lead
            </button>
            <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 12 }}>
              Shows the winner plus every rejected candidate, the pincode/district/state the engine
              actually resolved, and which toggle-state applied. Set an expected owner to turn a run
              into a pass/fail check, or switch to the batch grid to sweep a whole state list at once.
            </p>
          </div>
        )}
        {tab === 'advanced' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'var(--ivory-0)', border: '1px solid var(--line)', borderRadius: 10, padding: '10px 13px', marginBottom: 16, fontSize: 12, color: 'var(--ink-2)' }}>
              <SlidersHorizontal size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>Power-user view — the raw category / territory / overlay rule tables. Most editing now happens in the wizard &amp; coverage screens above.</div>
            </div>
            <StructuralRulesPanel coverage={coverage} onRefresh={load} />
          </div>
        )}
      </div>

      {/* ── Simulator drawer (reused) ── */}
      <SimulatorDrawer open={simOpen} onClose={() => setSimOpen(false)} staff={coverage.staff} dealers={coverage.dealers} categoryRules={coverage.categoryRules} onResult={() => {}} />

      {/* ── Assignment wizard (M4a/b/c) ── */}
      {wizard && (
        <AssignmentWizard
          layer={wizard.layer}
          member={wizard.member}
          staff={coverage.staff}
          groups={groups.length ? groups : deriveGroupsFromMatrix(matrix)}
          matrix={matrix}
          fallbackHint={coverage.staff.find((s) => /deepika/i.test(s.name))?.id ?? null}
          onClose={() => setWizard(null)}
          onSaved={() => { setWizard(null); void load(); }}
        />
      )}

      {/* ── Coverage-panel overlay: M5 Escalation editor scoped to a manager ── */}
      {escalationOverlay && (
        <div style={overlayStyle} role="dialog" aria-modal="true" onClick={() => setEscalationOverlay(null)}>
          <div style={{ ...drawerStyle, maxWidth: 900 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--line)' }}>
              <div style={{ flex: 1, fontSize: 16, fontWeight: 700, color: 'var(--ink-0)' }}>Edit escalation</div>
              <button type="button" onClick={() => { setEscalationOverlay(null); void load(); }} style={iconBtn}><X size={16} /></button>
            </div>
            <div style={{ padding: 18, overflow: 'auto', flex: 1 }}>
              <EscalationTab coverage={coverage} groups={groups} initialManagerId={escalationOverlay} />
            </div>
          </div>
        </div>
      )}

      {/* ── Coverage-panel overlay: existing dealer modal scoped to a manager ── */}
      {dealerOverlay && (
        <DealerMembershipModal
          allDealers={coverage.dealers}
          managerId={dealerOverlay}
          nameOf={(id) => coverage.staff.find((s) => s.id === id)?.name ?? id}
          onClose={() => { setDealerOverlay(null); void load(); }}
        />
      )}
    </div>
  );
}

// ═══════════════ M1 Coverage Matrix ═══════════════
function CoverageMatrixTab({ matrix, onGap }: { matrix: CoverageMatrixV2 | null; onGap: () => void }) {
  if (!matrix) {
    return <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Coverage matrix unavailable — seed product groups &amp; assignments first (Wave-1 seed).</div>;
  }
  const cellByKey = useMemo(() => {
    const m = new Map<string, ReturnType<typeof classifyMatrixCell>>();
    for (const c of matrix.cells) m.set(`${c.productGroupKey}|${c.state}`, classifyMatrixCell(c));
    return m;
  }, [matrix]);
  const t = matrix.totals;
  const covPct = matrix.cells.length ? Math.round(((matrix.cells.length - t.gap) / matrix.cells.length) * 100) : 100;

  return (
    <div>
      <SectionHead title="Coverage Matrix" sub="Every product category × state. Green = owned, red hatch = GAP (falls to Fallback), amber = exec-overlap (pick a primary), violet = manager-overlap → triage, saffron dot = a dealer also covers it." />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
        <Stat v={t.gap} k="Coverage gaps" tone="gap" />
        <Stat v={t.execOverlap} k="Exec-overlaps (pick primary)" tone="overlap" />
        <Stat v={t.managerOverlap} k="Mgr-overlap → triage" tone="triage" />
        <Stat v={`${covPct}%`} k="Category×state coverage" tone="owned" />
        <Stat v={`${matrix.groups.length} · ${matrix.states.length}`} k="Products · States" tone="neutral" />
      </div>

      <div style={{ overflow: 'auto', border: '1px solid var(--line)', borderRadius: 10, background: 'var(--ivory-0)' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, position: 'sticky', left: 0, background: 'var(--ivory-0)', textAlign: 'left' }}>Product ╲ State</th>
              {matrix.states.map((s) => <th key={s} style={thStyle}>{s}</th>)}
            </tr>
          </thead>
          <tbody>
            {matrix.groups.map((g) => (
              <tr key={g}>
                <td style={{ ...tdStyle, fontWeight: 700, position: 'sticky', left: 0, background: 'var(--ivory-0)', textAlign: 'left', whiteSpace: 'nowrap' }}>{g}</td>
                {matrix.states.map((s) => {
                  const v = cellByKey.get(`${g}|${s}`);
                  if (!v) return <td key={s} style={tdStyle} />;
                  const clickable = v.tone === 'gap' || v.tone === 'overlap';
                  return (
                    <td key={s} title={v.label}
                      onClick={clickable ? onGap : undefined}
                      style={{ ...tdStyle, background: TONE_BG[v.tone], color: TONE_FG[v.tone], cursor: clickable ? 'pointer' : 'default', textAlign: 'center', position: 'relative' }}>
                      {v.tone === 'owned' ? '●' : v.tone === 'gap' ? '—' : v.tone === 'overlap' ? '⚑' : '⇄'}
                      {v.dealerDot && <span style={{ position: 'absolute', top: 3, right: 4, width: 6, height: 6, borderRadius: 3, background: 'var(--saffron, #d98a25)' }} />}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 14, fontSize: 11.5, color: 'var(--ink-2)' }}>
        <Legend sw={TONE_BG.owned} label="Owned (member)" />
        <Legend sw={TONE_BG.gap} label="Gap → Fallback (Deepika triage)" />
        <Legend sw={TONE_BG.overlap} label="Exec-overlap · resolved by primary" />
        <Legend sw={TONE_BG.triage} label="Manager-overlap · resolved by triage" />
        <Legend sw="var(--saffron, #d98a25)" label="Dealer also covers" />
      </div>

      {/* Three destinations */}
      <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--ink-3)', margin: '18px 0 6px' }}>
        Where a lead can land — three first-class destinations
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 10 }}>
        <Dest ico="👤" title="Live human owner" sub="base member · Layer 1" />
        <Dest ico="🤝" title="Dealer (assignment)" sub="rep always OWNS · dealer is assigned to work it · Layer 3" />
        <Dest ico="☎" title="Triage calling queue" sub="COLD / no-reply / incomplete → human calls to qualify" />
      </div>

      <NoteBox tone="red" style={{ marginTop: 12 }}>
        <b>Safety primitive — Mandatory fallback.</b> Any product+state with no owner routes to the Fallback owner (Deepika · triage), so an unclaimed lead never vanishes. Gaps are highlighted so you fix ownership, not so leads are lost.
      </NoteBox>
    </div>
  );
}

// ═══════════════ M2 Teams ═══════════════
function TeamsTab({ coverage, onOpenTeam }: { coverage: CoverageData; onOpenTeam: (managerId: string) => void }) {
  const { staff, staffGraph } = coverage;
  const nameOf = (id: string) => staff.find((s) => s.id === id)?.name ?? id;
  const managers = Object.keys(staffGraph.execByManager);

  return (
    <div>
      <SectionHead title="Teams & Roles" sub="A team = one manager + its members (executives / coordinators), covering product categories over a territory." />
      {managers.length === 0 && <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>No teams found — set team leads (manager roles) in People &amp; Teams.</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12 }}>
        {managers.map((mgrId) => {
          // execByManager may be manager→exec-id (string, legacy staffGraph) OR manager→exec-id[] — coerce.
          const _rawE = staffGraph.execByManager[mgrId] as unknown;
          const execs: string[] = Array.isArray(_rawE) ? (_rawE as string[]) : _rawE ? [String(_rawE)] : [];
          return (
            <button key={mgrId} type="button" onClick={() => onOpenTeam(mgrId)}
              style={{ textAlign: 'left', border: '1px solid var(--line)', borderRadius: 12, background: 'var(--ivory-0)', padding: 14, cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ width: 32, height: 32, borderRadius: 16, background: 'var(--forest,#2f7d3b)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700 }}>
                  {nameOf(mgrId).split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
                </span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-0)' }}>{nameOf(mgrId)}’s Team</div>
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{execs.length} member{execs.length === 1 ? '' : 's'}</div>
                </div>
                <ChevronRight size={16} style={{ marginLeft: 'auto', color: 'var(--ink-3)' }} />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {execs.slice(0, 6).map((e) => (
                  <span key={e} style={pillStyle}>{nameOf(e)}</span>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════ M3 Team detail ═══════════════
function TeamDetail({ coverage, managerId, onBack, onEditMember, openWizard, navigate, reload }: {
  coverage: CoverageData; managerId: string; onBack: () => void;
  onEditMember: (m: RouterStaff) => void;
  openWizard: (w: { layer: 'EXEC' | 'MANAGER'; member: RouterStaff | null }) => void;
  navigate: ReturnType<typeof useNavigate>; reload: () => void;
}) {
  const { staff, staffGraph, dealers } = coverage;
  const [dealerModal, setDealerModal] = useState(false);
  const nameOf = (id: string) => staff.find((s) => s.id === id)?.name ?? id;
  const managerStaff = staff.find((s) => s.id === managerId) ?? { id: managerId, name: nameOf(managerId), roleName: 'manager' } as RouterStaff;
  const myDealers = dealers.filter((d) => d.managerUserId === managerId);
  const _rawE = staffGraph.execByManager[managerId] as unknown;
  const _execIds: string[] = Array.isArray(_rawE) ? (_rawE as string[]) : _rawE ? [String(_rawE)] : [];
  const execs = _execIds.map((id) => staff.find((s) => s.id === id)).filter(Boolean) as RouterStaff[];

  return (
    <div>
      <button type="button" onClick={onBack} style={{ ...btnStyle, marginBottom: 12 }}><ArrowLeft size={13} /> All teams</button>
      <SectionHead title={`${nameOf(managerId)}’s Team`} sub={`Manager ${nameOf(managerId)} · ${execs.length} member${execs.length === 1 ? '' : 's'}. Click a member to edit their assignment (opens the wizard).`} />

      {/* Manager's own territories — managers can own Layer-1 directly AND have a Layer-2 escalation zone (drawn independently) */}
      <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--ink-3)', margin: '4px 0 8px' }}>Manager</div>
      <div style={{ border: '1px solid var(--line)', borderRadius: 12, background: 'var(--ivory-0)', padding: 14, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{ width: 32, height: 32, borderRadius: 16, background: 'var(--saffron,#b9791e)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700 }}>
            {nameOf(managerId).split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
          </span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-0)' }}>{nameOf(managerId)}</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>Team manager</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button type="button" onClick={() => openWizard({ layer: 'EXEC', member: managerStaff })} style={{ ...btnStyle, fontSize: 12 }}>
            <Grid3x3 size={13} /> Edit base territory (manager sells)
          </button>
          <button type="button" onClick={() => openWizard({ layer: 'MANAGER', member: managerStaff })} style={{ ...btnStyle, fontSize: 12 }}>
            <TrendingUp size={13} /> Edit escalation territory
          </button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8 }}>
          <b>Base</b> = leads the manager owns directly (Layer 1). <b>Escalation</b> = where HOT / untouched leads route up to them (Layer 2). Drawn independently — may match, overlap, or differ from the execs’.
        </div>
      </div>

      <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--ink-3)', margin: '4px 0 8px' }}>Members</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 12, marginBottom: 20 }}>
        {execs.map((m) => (
          <button key={m.id} type="button" onClick={() => onEditMember(m)}
            style={{ textAlign: 'left', border: '1px solid var(--line)', borderRadius: 12, background: 'var(--ivory-0)', padding: 12, cursor: 'pointer' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-0)' }}>{m.name}</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{m.roleName ?? 'executive'}</div>
          </button>
        ))}
      </div>

      {/* Dealers under this manager — S8's M6 surface */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--ink-3)' }}>Dealers under this manager</div>
        <div style={{ flex: 1 }} />
        <button type="button" onClick={() => setDealerModal(true)} style={{ ...btnStyle, padding: '5px 10px', fontSize: 12 }}>
          <Store size={13} /> ＋ Add / remove dealers
        </button>
      </div>
      <NoteBox tone="forest" style={{ marginBottom: 12, fontSize: 11.5 }}>
        <b>Dealers belong to the manager.</b> A dealer never owns a lead — the rep stays owner-of-record; a dealer only receives an <b>assignment</b> to work / quote it. Manage pincode territory &amp; details on the{' '}
        <button type="button" onClick={() => navigate(`/dealer-management?manager=${managerId}`)} style={linkBtn}>Dealer Management screen</button>.
      </NoteBox>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {myDealers.map((d) => <span key={d.id} style={{ ...pillStyle, background: 'var(--warn-bg,#f7eccf)', color: 'var(--warn,#a5741a)' }}>{d.name}</span>)}
        {myDealers.length === 0 && <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>No dealers assigned to this manager yet — click “Add / remove dealers”.</span>}
      </div>
      {dealerModal && (
        <DealerMembershipModal
          allDealers={dealers}
          managerId={managerId}
          nameOf={nameOf}
          onClose={() => { setDealerModal(false); reload(); }}
        />
      )}
    </div>
  );
}

// ═══════════════ Dealer membership modal (add / remove dealers for a manager) ═══════════════
function DealerMembershipModal({ allDealers, managerId, nameOf, onClose }: {
  allDealers: CoverageData['dealers']; managerId: string; nameOf: (id: string) => string; onClose: () => void;
}) {
  const [assigned, setAssigned] = useState<Set<string>>(
    () => new Set(allDealers.filter((d) => d.managerUserId === managerId).map((d) => d.id)),
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const shown = allDealers.filter((d) => d.name.toLowerCase().includes(q.trim().toLowerCase()));

  async function toggle(d: CoverageData['dealers'][number]) {
    const isOn = assigned.has(d.id);
    setBusy(d.id); setErr(null);
    try {
      await leadRouterApi.patchDealer(d.id, { managerUserId: isOn ? null : managerId });
      setAssigned((prev) => { const n = new Set(prev); if (isOn) n.delete(d.id); else n.add(d.id); return n; });
    } catch (e) {
      // Surface the dealer-requires-exec guard (spec §6): a 422 NO_EXEC_OWNER from
      // the backend renders the locked "assign an exec first" copy; else generic.
      setErr(`Couldn't update ${d.name}. ${dealerAssignErrorMessage(e, (e as Error)?.message ?? '')}`);
    } finally { setBusy(null); }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,15,10,0.4)', display: 'grid', placeItems: 'center', zIndex: 1000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(560px, 96vw)', maxHeight: '82vh', display: 'flex', flexDirection: 'column', background: 'var(--ivory-0)', border: '1px solid var(--line)', borderRadius: 14, boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-0)' }}>Add / remove dealers</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
            Checked dealers belong to <b>{nameOf(managerId)}</b>. Check to add, uncheck to remove — changes save instantly.
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dealers…"
            style={{ marginTop: 10, width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--ivory-1)', fontSize: 13, color: 'var(--ink-0)' }} />
        </div>
        {err && <div style={{ padding: '8px 18px', color: 'var(--danger, #b23c30)', fontSize: 12 }}>{err}</div>}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '8px 10px' }}>
          {shown.length === 0 && <div style={{ padding: 16, color: 'var(--ink-3)', fontSize: 13 }}>No dealers match.</div>}
          {shown.map((d) => {
            const on = assigned.has(d.id);
            const otherMgr = !on && d.managerUserId && d.managerUserId !== managerId ? nameOf(d.managerUserId) : null;
            return (
              <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 8, cursor: busy === d.id ? 'wait' : 'pointer', opacity: busy === d.id ? 0.55 : 1 }}>
                <input type="checkbox" checked={on} disabled={busy === d.id} onChange={() => void toggle(d)} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink-0)' }}>{d.name}</span>
                {otherMgr && <span style={{ fontSize: 11, color: 'var(--warn, #a5741a)' }}>· with {otherMgr} (checking reassigns)</span>}
              </label>
            );
          })}
        </div>
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1, fontSize: 12, color: 'var(--ink-3)' }}>{assigned.size} dealer{assigned.size === 1 ? '' : 's'} assigned to this manager</span>
          <button type="button" onClick={onClose} style={{ ...btnStyle, background: 'var(--forest)', color: '#fff', border: 'none' }}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════ M5 Escalation editor ═══════════════
function EscalationTab({ coverage, groups, initialManagerId }: { coverage: CoverageData; groups: RoutingProductGroupV2[]; initialManagerId?: string }) {
  const { staff, staffGraph } = coverage;
  const managers = Object.keys(staffGraph.execByManager);
  const nameOf = (id: string) => staff.find((s) => s.id === id)?.name ?? id;
  const [selMgr, setSelMgr] = useState<string | null>(
    (initialManagerId && managers.includes(initialManagerId)) ? initialManagerId : (managers[0] ?? null),
  );
  const [configs, setConfigs] = useState<EscalationConfigV2[]>([]);
  const [deConfigs, setDeConfigs] = useState<DeescalationConfigV2[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!selMgr) return;
    void leadRouterV2Api.getEscalation(selMgr).then(setConfigs).catch(() => setConfigs([]));
    void leadRouterV2Api.getDeescalation(selMgr).then(setDeConfigs).catch(() => setDeConfigs([]));
  }, [selMgr]);

  const ladderGroups = groups.filter((g) => g.temperatureLadderEnabled);
  const shownGroups = ladderGroups.length ? ladderGroups : groups;
  const isOn = (key: string) => {
    const row = configs.find((c) => c.productGroupKey === key);
    return row ? row.enabled : true; // default ON when no row
  };
  // LOG5 — de-escalation default is OFF when no row (safe default, opposite of escalation).
  const isDeOn = (key: string) => {
    const row = deConfigs.find((c) => c.productGroupKey === key);
    return row ? row.enabled : false;
  };
  const flip = async (key: string, v: boolean) => {
    if (!selMgr) return;
    setBusy(true);
    try {
      await leadRouterV2Api.putEscalation({ managerUserId: selMgr, productGroupKey: key, enabled: v });
      setConfigs(await leadRouterV2Api.getEscalation(selMgr));
    } finally { setBusy(false); }
  };
  const flipDe = async (key: string, v: boolean) => {
    if (!selMgr) return;
    setBusy(true);
    try {
      await leadRouterV2Api.putDeescalation({ managerUserId: selMgr, productGroupKey: key, enabled: v });
      setDeConfigs(await leadRouterV2Api.getDeescalation(selMgr));
    } finally { setBusy(false); }
  };

  return (
    <div>
      <SectionHead title="Manager Escalation editor" sub="When a new, untouched lead goes HOT (Avika tier) or is flagged B2B, it auto-moves to that exec's own team manager. If a human already engaged the lead, the manager is notified only — never yanked (freeze-on-touch). Toggle per manager/product." />
      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 20 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 8 }}>Managers</div>
          {managers.map((m) => (
            <button key={m} type="button" onClick={() => setSelMgr(m)}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 11px', marginBottom: 6, borderRadius: 9, cursor: 'pointer',
                border: `1px solid ${selMgr === m ? 'var(--forest,#2f7d3b)' : 'var(--line)'}`, background: selMgr === m ? 'var(--forest-100,#d6ecd9)' : 'var(--ivory-0)', fontSize: 13, fontWeight: selMgr === m ? 700 : 500, color: 'var(--ink-1)' }}>
              {nameOf(m)}
            </button>
          ))}
        </div>
        <div style={{ border: '1px solid var(--line)', borderRadius: 12, background: 'var(--ivory-0)', padding: 16, opacity: busy ? 0.6 : 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-0)', marginBottom: 4 }}>{selMgr ? nameOf(selMgr) : '—'} · escalation</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginBottom: 14 }}>Manager territory is drawn independently — may match, overlap, or differ from the execs'. There is no "manager = broader zone" rule.</div>
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 8 }}>Escalation per product</div>
          <div style={{ border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
            {shownGroups.map((g) => {
              const on = isOn(g.key);
              return (
                <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid var(--line)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-1)', minWidth: 130 }}>{g.label}</div>
                  {on
                    ? <><span style={{ ...pillStyle, background: 'var(--forest-100,#d6ecd9)', color: 'var(--forest,#2f7d3b)' }}>Untouched → Manager</span><span style={pillStyle}>Touched → notify-only</span></>
                    : <span style={pillStyle}>Escalation OFF (stays with exec)</span>}
                  <div style={{ marginLeft: 'auto' }}><Toggle on={on} onChange={(v) => flip(g.key, v)} /></div>
                </div>
              );
            })}
            {shownGroups.length === 0 && <div style={{ padding: 12, fontSize: 12, color: 'var(--ink-3)' }}>No product groups seeded.</div>}
          </div>

          {/* LOG5 — COLD → triage de-escalation (mirror of escalation, safe default-OFF). */}
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 4 }}>De-escalate COLD leads to triage</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginBottom: 8 }}>When ON, leads Avika-scored COLD at routing time go to the triage queue instead of a rep. Default OFF.</div>
          <div style={{ border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
            {shownGroups.map((g) => {
              const on = isDeOn(g.key);
              return (
                <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid var(--line)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-1)', minWidth: 130 }}>{g.label}</div>
                  {on
                    ? <span style={{ ...pillStyle, background: 'var(--sky-100,#d6e6f2)', color: 'var(--sky,#2f6f9d)' }}>COLD → ☎ Triage</span>
                    : <span style={pillStyle}>De-escalation OFF (COLD → exec)</span>}
                  <div style={{ marginLeft: 'auto' }}><Toggle on={on} onChange={(v) => flipDe(g.key, v)} /></div>
                </div>
              );
            })}
            {shownGroups.length === 0 && <div style={{ padding: 12, fontSize: 12, color: 'var(--ink-3)' }}>No product groups seeded.</div>}
          </div>

          <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 8 }}>Temperature ladder preview</div>
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 8, flexWrap: 'wrap' }}>
            <LadderBox k="COLD / NO-REPLY" v="→ ☎ Triage calling queue" s="No-reply / incomplete leads go to the triage CALLING queue — Deepika calls to qualify." tone="cold" />
            <LadderBox k="WARM" v="→ Executive (Layer 1)" s="Base owner by product × territory." tone="warm" />
            <LadderBox k="HOT / B2B" v="Untouched → Team Manager · Touched → notify-only" s="Trigger = Avika tier + B2B flag. Never rep-declared." tone="hot" />
          </div>
          <NoteBox tone="violet" style={{ marginTop: 14 }}>
            <b>Manager-layer tie-break.</b> If two managers cover the same product+area, escalation goes to <b>triage — never auto-picked</b>. (Distinct from the exec layer, which resolves overlaps by most-specific territory + primary.)
          </NoteBox>
        </div>
      </div>
    </div>
  );
}

// ═══════════════ M4a/b/c Assignment Wizard ═══════════════
function AssignmentWizard({ layer, member, staff, groups, matrix, fallbackHint, onClose, onSaved }: {
  layer: 'EXEC' | 'MANAGER'; member: RouterStaff | null; staff: RouterStaff[];
  groups: RoutingProductGroupV2[]; matrix: CoverageMatrixV2 | null;
  fallbackHint: string | null; onClose: () => void; onSaved: () => void;
}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [userId, setUserId] = useState<string | null>(member?.id ?? null);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [mode, setMode] = useState<'unified' | 'perprod'>('unified');
  const [selectedDistricts, setSelectedDistricts] = useState<Set<string>>(new Set());
  const [radiusCenter, setRadiusCenter] = useState('Panipat');
  const [radiusKm, setRadiusKm] = useState(100);
  const [radiusMsg, setRadiusMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [gaps, setGaps] = useState<Array<{ productGroupKey: string; matchType: string; matchValue: string }>>([]);

  const states = matrix?.states ?? [];

  const wizardState: WizardState = {
    layer, userId,
    mode,
    products: selectedProducts,
    unified: mode === 'unified' ? { districts: [...selectedDistricts] } : undefined,
    perProduct: mode === 'perprod'
      ? Object.fromEntries(selectedProducts.map((p) => [p, { districts: [...selectedDistricts] }]))
      : undefined,
  };
  const emptyProducts = emptyTerritoryProducts(wizardState);

  const runRadius = async () => {
    setRadiusMsg('Expanding…');
    try {
      const res = await leadRouterV2Api.expandRadius({ center: radiusCenter, km: radiusKm });
      setSelectedDistricts((prev) => applyRadiusExpansion(res.districts, prev));
      setRadiusMsg(`Added ${res.districts.length} district(s) within ${radiusKm} km of ${radiusCenter}.`);
    } catch {
      setRadiusMsg('Radius expansion needs the S9 geo endpoint (district centroids) — not wired on this box yet.');
    }
  };

  const save = async () => {
    setSaving(true); setSaveErr(null); setGaps([]);
    try {
      const assignments = buildBulkPayload(wizardState);
      const res = await leadRouterV2Api.bulkSaveAssignments({ assignments, fallbackUserId: fallbackHint });
      if (res.gaps?.length) setGaps(res.gaps);
      onSaved();
    } catch (e: any) {
      const body = e?.response?.data;
      if (e?.response?.status === 422 && body?.gaps) {
        setGaps(body.gaps);
        setSaveErr(body.error ?? 'Config leaves an area with no owner and no fallback.');
      } else {
        setSaveErr(body?.error ?? e?.message ?? 'Save failed');
      }
    } finally { setSaving(false); }
  };

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true">
      <div style={{ ...drawerStyle, maxWidth: 860 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--ink-3)' }}>Assignment Wizard · {member?.name ?? 'New assignment'} · {layer}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink-0)' }}>
              {step === 1 ? 'Step 1 — Product categories' : step === 2 ? 'Step 2 — Territory mode' : 'Step 3 — States → Districts'}
            </div>
          </div>
          <button type="button" onClick={onClose} style={iconBtn}><X size={16} /></button>
        </div>

        <div style={{ padding: 18, overflow: 'auto', flex: 1 }}>
          {/* member picker when opened without a member */}
          {!member && step === 1 && (
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Assign to</label>
              <select value={userId ?? ''} onChange={(e) => setUserId(e.target.value || null)} style={selectStyle}>
                <option value="">— pick a person —</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}

          {step === 1 && (
            <>
              <p style={pStyle}>Pick the products this member will own.</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {groups.map((g) => {
                  const on = selectedProducts.includes(g.key);
                  return (
                    <button key={g.id} type="button"
                      onClick={() => setSelectedProducts((p) => on ? p.filter((x) => x !== g.key) : [...p, g.key])}
                      style={{ ...chipStyle, background: on ? 'var(--forest-100,#d6ecd9)' : 'var(--ivory-0)', borderColor: on ? 'var(--forest,#2f7d3b)' : 'var(--line)', color: on ? 'var(--forest,#2f7d3b)' : 'var(--ink-1)', fontWeight: on ? 700 : 500 }}>
                      {g.label}
                    </button>
                  );
                })}
                {groups.length === 0 && <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>No product groups — seed them first.</span>}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p style={pStyle}>One territory for every product this member owns, or a different territory per product.</p>
              <div style={{ display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 9, overflow: 'hidden' }}>
                {(['unified', 'perprod'] as const).map((m) => (
                  <button key={m} type="button" onClick={() => setMode(m)}
                    style={{ padding: '8px 14px', fontSize: 13, cursor: 'pointer', border: 'none', background: mode === m ? 'var(--forest,#2f7d3b)' : 'var(--ivory-0)', color: mode === m ? '#fff' : 'var(--ink-2)' }}>
                    {m === 'unified' ? 'One territory for all products' : 'Different territory per product'}
                  </button>
                ))}
              </div>
              {mode === 'perprod' && emptyProducts.length > 0 && (
                <NoteBox tone="red" style={{ marginTop: 12 }}>
                  Products with an <b>empty</b> territory (red dot) must be fixed before saving (Safety #1): {emptyProducts.join(', ')}.
                </NoteBox>
              )}
              <p style={{ ...pStyle, marginTop: 12 }}>Granularity = District (India ~780 districts; add a city only where you need finer than district).</p>
            </>
          )}

          {step === 3 && (
            <>
              <p style={pStyle}>Selecting a state pre-checks all its districts — uncheck the ones this member doesn't own.</p>

              {/* +100 km */}
              <div style={{ border: '1px solid var(--line)', borderRadius: 9, background: 'var(--ivory-1)', padding: 11, marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--ink-2)' }}>＋100 km from</span>
                  <input value={radiusCenter} onChange={(e) => setRadiusCenter(e.target.value)} style={{ ...inputStyle, width: 120 }} />
                  <input type="number" value={radiusKm} onChange={(e) => setRadiusKm(Number(e.target.value))} style={{ ...inputStyle, width: 64 }} />
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>km</span>
                  <button type="button" onClick={runRadius} style={{ ...btnStyle, background: 'var(--saffron,#d98a25)', color: '#fff', border: 'none', padding: '6px 12px' }}>Auto-select</button>
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 7 }}>{radiusMsg ?? 'District-centroid radius (Option A) — an admin shortcut; live lead matching stays name-based.'}</div>
              </div>

              {/* States → districts tri-state (districts stubbed to the state itself when the map lacks a district list) */}
              <div style={{ border: '1px solid var(--line)', borderRadius: 9, maxHeight: 260, overflow: 'auto' }}>
                {states.map((s) => {
                  const districts = districtsForState(matrix, s);
                  const tri = stateTriState(s, districts, selectedDistricts);
                  return (
                    <div key={s} style={{ borderBottom: '1px solid var(--line)' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', fontWeight: 600, fontSize: 13, color: 'var(--ink-1)' }}>
                        <input type="checkbox" checked={tri === 'all'} ref={(el) => { if (el) el.indeterminate = tri === 'some'; }}
                          onChange={() => setSelectedDistricts(toggleState(s, districts, selectedDistricts))} />
                        {s} <span style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 400 }}>({tri})</span>
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 12px 8px 30px' }}>
                        {districts.map((d) => {
                          const on = selectedDistricts.has(districtKey(s, d));
                          return (
                            <button key={d} type="button" onClick={() => setSelectedDistricts(toggleDistrict(s, d, selectedDistricts))}
                              style={{ ...chipStyle, padding: '3px 9px', fontSize: 11, background: on ? 'var(--forest-100,#d6ecd9)' : 'var(--ivory-0)', borderColor: on ? 'var(--forest,#2f7d3b)' : 'var(--line)', color: on ? 'var(--forest,#2f7d3b)' : 'var(--ink-2)' }}>
                              {d}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {states.length === 0 && <div style={{ padding: 12, fontSize: 12, color: 'var(--ink-3)' }}>No states in the coverage universe yet — seed CityDistrictMap / assignments first.</div>}
              </div>

              <NoteBox tone="neutral" style={{ marginTop: 10 }}>
                Custom areas cover carve-outs like <span style={{ fontFamily: 'monospace' }}>MP excl Indore/Bhopal</span> — a state selection minus deny-listed cities. Optional pincode city-split (e.g. split Delhi among reps) needs pincode capture at intake — <b>pending Sun Stellar approval</b>.
              </NoteBox>

              {gaps.length > 0 && (
                <NoteBox tone="red" style={{ marginTop: 10 }}>
                  <b>Safety #1 — {saveErr ? 'blocked' : 'gaps'}.</b> {saveErr ?? 'These product+areas have no owner (covered by fallback):'}{' '}
                  {gaps.map((g) => `${g.productGroupKey}·${g.matchValue}`).join(', ')}
                </NoteBox>
              )}
              {saveErr && gaps.length === 0 && <NoteBox tone="red" style={{ marginTop: 10 }}>{saveErr}</NoteBox>}
            </>
          )}
        </div>

        {/* footer stepper */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', borderTop: '1px solid var(--line)', background: 'var(--ivory-1)' }}>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {selectedProducts.length} product(s) · {selectedDistricts.size} district(s) · mode {mode}
          </span>
          <div style={{ flex: 1 }} />
          {step > 1 && <button type="button" onClick={() => setStep((s) => (s - 1) as any)} style={btnStyle}>Back</button>}
          {step < 3 && <button type="button" disabled={step === 1 && (selectedProducts.length === 0 || !userId)}
            onClick={() => setStep((s) => (s + 1) as any)}
            style={{ ...btnStyle, background: 'var(--forest,#2f7d3b)', color: '#fff', border: 'none', opacity: (step === 1 && (selectedProducts.length === 0 || !userId)) ? 0.5 : 1 }}>
            Next ▸
          </button>}
          {step === 3 && <button type="button" onClick={save} disabled={saving}
            style={{ ...btnStyle, background: 'var(--saffron,#d98a25)', color: '#fff', border: 'none', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : '✓ Save assignment'}
          </button>}
        </div>
      </div>
    </div>
  );
}

// ── helpers / tiny presentational bits ───────────────────────────────────────
function deriveGroupsFromMatrix(matrix: CoverageMatrixV2 | null): RoutingProductGroupV2[] {
  return (matrix?.groups ?? []).map((k, i) => ({ id: k, tenantId: '', key: k, label: k, sortOrder: i, temperatureLadderEnabled: false, active: true }));
}
function districtsForState(matrix: CoverageMatrixV2 | null, state: string): string[] {
  // The coverage matrix rolls up to state; without a district list we let the
  // state stand in as its own single "district" so tri-state still works.
  return [state];
}

function SectionHead({ title, sub }: { title: string; sub: string }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink-0)', margin: '0 0 4px' }}>{title}</h2>
      <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: 0, maxWidth: 720, lineHeight: 1.5 }}>{sub}</p>
    </div>
  );
}
function Stat({ v, k, tone }: { v: React.ReactNode; k: string; tone: CellTone | 'neutral' }) {
  const fg = tone === 'neutral' ? 'var(--ink-1)' : TONE_FG[tone];
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, background: 'var(--ivory-0)', padding: '10px 12px' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: fg }}>{v}</div>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{k}</div>
    </div>
  );
}
function Legend({ sw, label }: { sw: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 14, height: 14, borderRadius: 4, background: sw, border: '1px solid var(--line)', display: 'inline-block' }} /> {label}
    </span>
  );
}
function Dest({ ico, title, sub }: { ico: string; title: string; sub: string }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', border: '1px solid var(--line)', borderRadius: 10, background: 'var(--ivory-0)', padding: '10px 12px' }}>
      <span style={{ fontSize: 20 }}>{ico}</span>
      <div><div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-0)' }}>{title}</div><div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{sub}</div></div>
    </div>
  );
}
function LadderBox({ k, v, s, tone }: { k: string; v: string; s: string; tone: 'cold' | 'warm' | 'hot' }) {
  const bg = tone === 'cold' ? 'var(--violet-50,#ece7fb)' : tone === 'warm' ? 'var(--ivory-0)' : 'var(--warn-bg,#f7eccf)';
  return (
    <div style={{ flex: '1 1 180px', minWidth: 160, border: '1px solid var(--line)', borderRadius: 10, background: bg, padding: '10px 12px' }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', color: 'var(--ink-2)' }}>{k}</div>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink-0)', margin: '3px 0' }}>{v}</div>
      <div style={{ fontSize: 10.5, color: 'var(--ink-3)', lineHeight: 1.4 }}>{s}</div>
    </div>
  );
}
function NoteBox({ tone, style, children }: { tone: 'red' | 'forest' | 'violet' | 'neutral'; style?: React.CSSProperties; children: React.ReactNode }) {
  const map: Record<string, { bg: string; bd: string; fg: string }> = {
    red: { bg: '#fbe9e7', bd: '#f0c4bd', fg: 'var(--danger,#b5433a)' },
    forest: { bg: 'var(--forest-100,#d6ecd9)', bd: '#bcdcc0', fg: 'var(--forest,#2f7d3b)' },
    violet: { bg: 'var(--violet-50,#ece7fb)', bd: '#d5cbf5', fg: 'var(--violet,#6b4fd8)' },
    neutral: { bg: 'var(--ivory-0)', bd: 'var(--line)', fg: 'var(--ink-2)' },
  };
  const c = map[tone];
  return <div style={{ border: `1px solid ${c.bd}`, background: c.bg, color: c.fg, borderRadius: 10, padding: '10px 13px', fontSize: 12, lineHeight: 1.5, ...style }}>{children}</div>;
}

const btnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', fontSize: 13, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--ivory-0)', cursor: 'pointer', color: 'var(--ink-1)' };
const iconBtn: React.CSSProperties = { display: 'grid', placeItems: 'center', width: 30, height: 30, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--ivory-0)', cursor: 'pointer', color: 'var(--ink-2)' };
const linkBtn: React.CSSProperties = { border: 'none', background: 'none', padding: 0, color: 'var(--forest,#2f7d3b)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' };
const pillStyle: React.CSSProperties = { fontSize: 11, padding: '3px 8px', borderRadius: 999, background: 'var(--ivory-1)', border: '1px solid var(--line)', color: 'var(--ink-2)' };
const thStyle: React.CSSProperties = { padding: '8px 10px', fontSize: 11, fontWeight: 700, color: 'var(--ink-2)', borderBottom: '1px solid var(--line)', whiteSpace: 'nowrap' };
const tdStyle: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid var(--line)', borderRight: '1px solid var(--line)' };
const pStyle: React.CSSProperties = { fontSize: 12.5, color: 'var(--ink-2)', margin: '0 0 12px', lineHeight: 1.5 };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--ink-2)', marginBottom: 5 };
const selectStyle: React.CSSProperties = { width: '100%', maxWidth: 320, height: 34, padding: '0 10px', fontSize: 13, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--ivory-0)', color: 'var(--ink-1)' };
const inputStyle: React.CSSProperties = { height: 30, padding: '0 9px', fontSize: 12.5, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--ivory-0)', color: 'var(--ink-0)' };
const chipStyle: React.CSSProperties = { padding: '6px 12px', fontSize: 12.5, border: '1px solid var(--line)', borderRadius: 999, background: 'var(--ivory-0)', cursor: 'pointer' };
const overlayStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.32)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000 };
const drawerStyle: React.CSSProperties = { width: '92%', maxWidth: 860, background: 'var(--ivory-1)', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 30px rgba(0,0,0,.15)' };

export default LeadRoutingPage;
